import type {
  Component as ReploComponent,
  ReploComponentType,
} from "schemas/component";
import type {
  ProductRef,
  ProductRefOrDynamic,
  StoreProductSummary,
} from "schemas/product";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedLabel from "@v1/components/designSystem/label/Label";
import Popover from "@v1/components/designSystem/popover/Popover";
import BundleDiscountsCustomPropModifier from "@v1/components/editor/customProp/BundleDiscountsCustomPropModifier";
import BundleProductsCustomPropModifier from "@v1/components/editor/customProp/BundleProductsCustomPropModifier";
import { DynamicDataSelector } from "@v1/components/editor/page/DynamicDataSelector";
import { ProductSelectionCombobox } from "@v1/components/editor/page/ProductSelectionCombobox";
import useDynamicDataQuickConnect from "@v1/hooks/useDynamicDataQuickConnect";
import { selectProductSummaries } from "@v1/reducers/commerce-reducer";
import { selectTemplateEditorProduct } from "@v1/reducers/template-reducer";
import { selectOpenPopoverId } from "@v1/reducers/ui-reducer";
import { useEditorSelector } from "@v1/store/store";
import { Database, Settings2 } from "lucide-react";
import { isContextRef } from "replo-runtime/store/ReploProduct";

const ConnectedProductsIndicatorTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    products: ProductRefOrDynamic[];
    isAncestor?: boolean;
    allowMultiple?: boolean;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ products, isAncestor, allowMultiple, onClick }, ref) => {
  const productSummariesFromStore = useEditorSelector(selectProductSummaries);
  const templateProduct = useEditorSelector(selectTemplateEditorProduct);
  if (allowMultiple && products.length < 1) {
    return (
      <button
        ref={ref}
        onClick={onClick}
        className="border-border flex h-[32px] items-center rounded-md border-[2px] border-dashed bg-white p-1.5"
      >
        <Database size="20" className="mr-1" />
        <DeprecatedLabel size="sm" label="Select Products" />
      </button>
    );
  }
  const productTilesToRender = products.map((productRef) => {
    if (!productRef) {
      return null;
    }
    if (isContextRef(productRef)) {
      return (
        templateProduct &&
        productSummariesFromStore[Number(templateProduct.productId)]
      );
    }
    return productSummariesFromStore[Number(productRef.productId)];
  });
  const isSingleProductOrEmpty = productTilesToRender.length <= 1;
  if (isSingleProductOrEmpty && !allowMultiple) {
    const productSummary = productTilesToRender[0];
    return (
      <button ref={ref} onClick={onClick} className="flex">
        <div
          className={twMerge(
            "border-purple flex h-[32px] w-[32px] items-center justify-center overflow-hidden bg-white rounded-md border-[2px]",
            // eslint-disable-next-line replo/no-tailwind-literal-color
            isAncestor && "border-purple-300",
            !productSummary?.featuredImage && "bg-white border-primary",
          )}
        >
          {productSummary?.featuredImage ? (
            <img src={productSummary.featuredImage} />
          ) : (
            <Database size="20" />
          )}
        </div>
      </button>
    );
  }
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={twMerge(
        "border-purple flex h-[32px] items-center rounded-sm bg-white border-[2px] p-1.5",
        // eslint-disable-next-line replo/no-tailwind-literal-color
        isAncestor && "border-purple-300",
      )}
    >
      <div className="mr-1 flex">
        {productTilesToRender
          .slice(0, 4)
          .map(
            (
              productSummary: StoreProductSummary | null | undefined,
              index: number,
            ) => (
              <div
                key={productSummary?.id ?? index}
                className={twMerge(
                  "border-solid-white flex h-[15px] w-[15px] items-center justify-center overflow-hidden rounded-sm",
                  !productSummary?.featuredImage && "bg-white border-primary",
                  index !== 0 && "ml-[-5px]",
                )}
              >
                {productSummary?.featuredImage ? (
                  <img src={productSummary.featuredImage} />
                ) : (
                  <Database size="20" />
                )}
              </div>
            ),
          )}
      </div>
      <DeprecatedLabel size="sm" label={`${products.length} Products`} />
    </button>
  );
});

ConnectedProductsIndicatorTrigger.displayName =
  "ConnectedProductsIndicatorTrigger";

const ProductSelectionControlBox = ({
  component,
  isAncestor,
}: {
  component: ReploComponent;
  isAncestor?: boolean;
}) => {
  const { dynamicDataProp, handleChangePropValue } =
    useDynamicDataQuickConnect(component);
  if (
    component.type !== "productCollection" &&
    (!component?.props._product ||
      (isContextRef(component.props._product) &&
        component.props._product.ref === "attributes._product"))
  ) {
    return null;
  }

  const isSingleProductProp = component.type === "product";
  if (isSingleProductProp) {
    if (dynamicDataProp && isContextRef(dynamicDataProp.propValue)) {
      return (
        <DynamicDataSelector
          targetType="productOnly"
          initialPath={dynamicDataProp.propValue.ref.split(".")}
          side="top"
          trigger={
            <ConnectedProductsIndicatorTrigger
              products={[dynamicDataProp.propValue]}
            />
          }
          onChange={handleChangePropValue}
        />
      );
    }
    const existingProductRef = dynamicDataProp?.propValue as ProductRef | null;
    return (
      <ProductSelectionCombobox
        value={existingProductRef}
        onChange={(value: ProductRef | null) => {
          return value && handleChangePropValue?.(value);
        }}
        allowAutoOpen
        side="top"
        trigger={
          <ConnectedProductsIndicatorTrigger
            products={existingProductRef ? [existingProductRef] : []}
            isAncestor={isAncestor}
          />
        }
      />
    );
  }
  return (
    <ProductSelectionCombobox
      isMultiselect
      value={(dynamicDataProp?.propValue as ProductRef[]) ?? []}
      onChange={(value: ProductRef[]) => {
        return value && handleChangePropValue?.(value as ProductRef[]);
      }}
      side="top"
      trigger={
        <ConnectedProductsIndicatorTrigger
          products={(dynamicDataProp?.propValue as ProductRefOrDynamic[]) ?? []}
          isAncestor={isAncestor}
        />
      }
    />
  );
};

const BundleTargetSelectionControlBox = ({
  component,
  isAncestor,
}: {
  component: ReploComponent;
  isAncestor?: boolean;
}) => {
  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const isDefaultOpen = openPopoverId === "bundle-product-selector";
  const productsWithVariantsArray = component.props.productsWithVariants ?? [];

  const productRefs = productsWithVariantsArray.map(({ productId }) => ({
    productId,
  }));
  return (
    <div className="flex gap-1.5">
      <Popover.Root
        shouldIgnoreOutsideInteractions={false}
        isDefaultOpen={isDefaultOpen}
      >
        <Popover.Trigger asChild>
          <ConnectedProductsIndicatorTrigger
            allowMultiple
            products={productRefs}
            isAncestor={isAncestor}
          />
        </Popover.Trigger>
        <Popover.Content
          stayInPosition
          shouldPreventDefaultOnInteractOutside={false}
          side="bottom"
          align="center"
          hideCloseButton
        >
          <BundleProductsCustomPropModifier component={component} />
        </Popover.Content>
      </Popover.Root>
      <Popover.Root shouldIgnoreOutsideInteractions={false}>
        <Popover.Trigger asChild>
          <button className="border-border flex h-[32px] w-[32px] bg-white items-center justify-center overflow-hidden rounded-md border-[2px]">
            <Settings2 size={16} />
          </button>
        </Popover.Trigger>
        <Popover.Content
          stayInPosition
          shouldPreventDefaultOnInteractOutside={false}
          side="bottom"
          align="center"
          hideCloseButton
          className="w-[300px]"
        >
          <BundleDiscountsCustomPropModifier component={component} />
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};

const PRODUCT_SELECTOR_COMPONENTS = new Set<ReploComponentType>([
  "product",
  "productCollection",
]);

export const BoundingBoxPropControls = ({
  component,
  isAncestor,
}: {
  component: ReploComponent;
  isAncestor?: boolean;
}) => {
  if (PRODUCT_SELECTOR_COMPONENTS.has(component.type)) {
    return (
      <div className="pointer-events-auto">
        <ProductSelectionControlBox
          component={component}
          isAncestor={isAncestor}
        />
      </div>
    );
  }
  if (component.type === "bundleBuilder") {
    return (
      <div className="pointer-events-auto">
        <BundleTargetSelectionControlBox
          component={component}
          isAncestor={isAncestor}
        />
      </div>
    );
  }
};
